import React, { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Badge, Input, RegionState } from '@freelancelabs/components';
import { MagnifyingGlass } from '@freelancelabs/icons';

import { ListSkeleton } from '../../components/Skeletons';
import { EligiblePartnersDocument } from '../../graphql/__generated__/types';
import { partnerListRefusalOf } from './refusals';

/** The bound the API applies too. Named here because the counter has to say it. */
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export interface PickedPartner {
    sourcePartnerId: string;
    name: string;
    externalReference: string | null;
}

interface Props {
    /** Set on the « add » modal, absent on the declaration one: it has no list yet. */
    excludePartnerListId?: string;
    picked: PickedPartner[];
    onPicked: (picked: PickedPartner[]) => void;
    /** « Rechercher un partenaire » on declaration, « … éligible » on addition. */
    searchPlaceholder: string;
    /** What « nothing left to offer » means, which differs between the two callers. */
    emptyTitle: string;
    emptyDescription: string;
}

/**
 * The selector of eligible partners, shared by the declaration modal and the addition
 * one. Written once because the two modals differ by their title and their button and by
 * nothing else — and because the three rules below are easy to get subtly wrong twice.
 *
 * **THE SEARCH GOES TO THE SERVER, and that is not a detail of implementation.** The
 * mockup filters an array of demonstration partners in the browser; the real referential
 * holds 4 444 eligible partners and the API serves 50 at a time. A filter applied to the
 * loaded page would answer « aucun partenaire » about a partner sitting on page four, and
 * the operator has no way of doubting it. Same rule as « Mes besoins » in the portal.
 *
 * **THE SELECTION SURVIVES THE SEARCH, and it is pinned to the top.** Tick a partner,
 * type something that no longer matches it, and it would leave the DOM: the counter would
 * then announce a partner the operator cannot see, let alone untick. So what is picked is
 * rendered first, always, and the rows below are the answer to the current search minus
 * what is already picked.
 *
 * **THE COUNTER SAYS BOTH NUMBERS.** How many are picked, and how many eligible partners
 * answer the search: fifty rows out of 4 444 look exactly like the whole referential.
 * Without the second number the operator refines nothing because they think they have
 * seen everything.
 */
const EligiblePartnerPicker = ({
    excludePartnerListId,
    picked,
    onPicked,
    searchPlaceholder,
    emptyTitle,
    emptyDescription,
}: Props) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const { data, loading, error } = useQuery(EligiblePartnersDocument, {
        variables: {
            search: debouncedSearch || null,
            excludePartnerListId: excludePartnerListId ?? null,
            limit: PAGE_SIZE,
            offset: 0,
        },
        fetchPolicy: 'cache-and-network',
    });

    const totalCount = data?.eligiblePartners?.totalCount ?? null;
    const items = data?.eligiblePartners?.items ?? [];
    const pickedIds = useMemo(() => new Set(picked.map((partner) => partner.sourcePartnerId)), [picked]);
    const unpicked = items.filter((partner) => !pickedIds.has(partner.sourcePartnerId));

    const toggle = (partner: PickedPartner) => {
        if (pickedIds.has(partner.sourcePartnerId)) {
            onPicked(picked.filter((entry) => entry.sourcePartnerId !== partner.sourcePartnerId));
            return;
        }
        onPicked([...picked, partner]);
    };

    const row = (partner: PickedPartner, isPicked: boolean) => (
        <label
            key={partner.sourcePartnerId}
            className="flex cursor-pointer items-center gap-3 border-b border-border-weak px-3 py-2 last:border-b-0 hover:bg-background-weak"
        >
            <input
                type="checkbox"
                checked={isPicked}
                onChange={() => toggle(partner)}
                className="size-4 shrink-0 accent-primary"
            />
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{partner.name}</span>
            {/* `CPT-…` is the reconciliation key, and it is what disambiguates the 386
                partners that share a name with another one. */}
            <span className="shrink-0 font-mono text-xs text-text-600">{partner.externalReference}</span>
        </label>
    );

    const refusal = error ? partnerListRefusalOf(error) : null;
    const showSkeleton = loading && items.length === 0 && picked.length === 0;

    return (
        <div className="flex flex-col gap-3">
            <Input
                value={search}
                onValue={setSearch}
                clearable
                prefix={<MagnifyingGlass size={16} />}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
            />

            <div className="max-h-72 overflow-y-auto rounded-md border border-border-norm">
                {refusal ? (
                    <RegionState tone="error" title={refusal.title} description={refusal.guidance} />
                ) : showSkeleton ? (
                    <p className="px-3 py-6 text-center text-sm text-text-600">
                        <ListSkeleton rows={5} withControl />
                    </p>
                ) : picked.length === 0 && unpicked.length === 0 ? (
                    <RegionState
                        title={debouncedSearch ? c('Partner picker').t`No eligible partner matches` : emptyTitle}
                        description={
                            debouncedSearch
                                ? c('Partner picker')
                                      .t`The search runs on the company name and on the Salesforce external identifier, never on the theme of a list.`
                                : emptyDescription
                        }
                    />
                ) : (
                    <>
                        {/* Pinned: see the header note. A ticked row must never leave. */}
                        {picked.map((partner) => row(partner, true))}
                        {unpicked.map((partner) => row(partner, false))}
                    </>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs text-text-600">
                <Badge size="small" category={picked.length > 0 ? 'info' : 'default'}>
                    {c('Partner picker').ngettext(
                        msgid`${picked.length} partner selected`,
                        `${picked.length} partners selected`,
                        picked.length
                    )}
                </Badge>
                {totalCount !== null && (
                    <span>
                        {totalCount > PAGE_SIZE
                            ? /* The truncation is stated, never implied: fifty rows out of
                                 4 444 look exactly like the whole referential. */
                              c('Partner picker')
                                  .t`${totalCount} eligible partners answer this search, and the first ${PAGE_SIZE} are shown. Refine the search to reach the others.`
                            : c('Partner picker').ngettext(
                                  msgid`${totalCount} eligible partner answers this search.`,
                                  `${totalCount} eligible partners answer this search.`,
                                  totalCount
                              )}
                    </span>
                )}
            </div>
        </div>
    );
};

export default EligiblePartnerPicker;
